import React, { useState } from "react";
import { Send, MessageCircle, Mail, MessageSquare, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { getCompanySettings } from "../lib/companySettings";
import { useI18n } from "../i18n/I18nContext";

function brl(v) {
  return `$${Number(v || 0).toFixed(2)}`;
}

/**
 * Build the message body shared by all channels.
 * Receives a generic record and a fallback "kind" label.
 */
async function buildMessage({ kind = "Invoice", clientName, total, address, link }) {
  const company = await getCompanySettings();
  const brand = company?.name || "Clean Manager USA";
  const lines = [
    `Olá ${clientName || ""}!`,
    "",
    `Segue o ${kind.toLowerCase()} da ${brand}.`,
  ];
  if (address) lines.push(`Endereço: ${address}`);
  if (total) lines.push(`Total: ${brl(total)}`);
  if (link) lines.push("", `Link: ${link}`);
  lines.push("", "Qualquer dúvida estamos à disposição.");
  if (company?.phone) lines.push(`📞 ${company.phone}`);
  return lines.join("\n");
}

function onlyDigits(s) {
  return String(s || "").replace(/\D/g, "");
}

function ensureE164US(phone) {
  const d = onlyDigits(phone);
  if (!d) return "";
  return d.length === 10 ? `1${d}` : d;
}

/**
 * Generic send menu.
 *
 *  <SendMenu
 *    kind="Invoice"            // human label used in the message
 *    record={estimate}         // raw object (uses .phone, .email, .name, .address, .total_value)
 *    link={publicLink}         // optional URL to include
 *    testIdPrefix="estimate-send-123"
 *  />
 */
export function SendMenu({ kind = "Invoice", record, link, testIdPrefix = "send" }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  const phone = record?.phone || "";
  const email = record?.email || "";
  const total = record?.total_value ?? record?.value;
  const address = record?.address;
  const clientName = record?.name || record?.client_name || "";

  async function sendWhatsApp() {
    const e164 = ensureE164US(phone);
    if (!e164) {
      toast.error(t("Cliente sem telefone cadastrado"));
      return;
    }
    const msg = await buildMessage({ kind, clientName, total, address, link });
    window.open(
      `https://wa.me/${e164}?text=${encodeURIComponent(msg)}`,
      "_blank",
      "noopener",
    );
    setOpen(false);
  }

  async function sendEmail() {
    if (!email) {
      toast.error(t("Cliente sem e-mail cadastrado"));
      return;
    }
    const company = await getCompanySettings();
    const subject = `${kind} - ${company?.name || "Clean Manager USA"}`;
    const body = await buildMessage({ kind, clientName, total, address, link });
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
    setOpen(false);
  }

  async function sendSMS() {
    const e164 = ensureE164US(phone);
    if (!e164) {
      toast.error(t("Cliente sem telefone cadastrado"));
      return;
    }
    const msg = await buildMessage({ kind, clientName, total, address, link });
    // The `sms:` scheme works on mobile (iOS/Android). Desktop will fallback to no-op.
    window.location.href = `sms:+${e164}?&body=${encodeURIComponent(msg)}`;
    setOpen(false);
  }

  async function copyLink() {
    if (!link) {
      toast.error(t("Nenhum link disponível para este registro"));
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t("Link copiado!"));
    } catch {
      // Fallback for non-secure contexts
      const ta = document.createElement("textarea");
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(t("Link copiado!"));
    }
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`${testIdPrefix}-trigger`}
          title={t("Enviar")}
          className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
        >
          <Send size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-56 p-1.5"
        data-testid={`${testIdPrefix}-menu`}
      >
        <MenuItem
          icon={<MessageCircle size={15} className="text-emerald-600" />}
          label="WhatsApp"
          onClick={sendWhatsApp}
          testId={`${testIdPrefix}-whatsapp`}
          dim={!phone}
        />
        <MenuItem
          icon={<Mail size={15} className="text-blue-600" />}
          label={t("E-mail")}
          onClick={sendEmail}
          testId={`${testIdPrefix}-email`}
          dim={!email}
        />
        <MenuItem
          icon={<MessageSquare size={15} className="text-amber-600" />}
          label={t("SMS (Texto)")}
          onClick={sendSMS}
          testId={`${testIdPrefix}-sms`}
          dim={!phone}
        />
        <div className="border-t border-slate-100 my-1" />
        <MenuItem
          icon={<Link2 size={15} className="text-slate-600" />}
          label={link ? t("Copiar link") : t("Sem link público")}
          onClick={copyLink}
          testId={`${testIdPrefix}-link`}
          dim={!link}
        />
      </PopoverContent>
    </Popover>
  );
}

function MenuItem({ icon, label, onClick, testId, dim }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-2.5 py-2 text-sm rounded-md text-left hover:bg-slate-100 transition-colors ${
        dim ? "opacity-60" : ""
      }`}
    >
      {icon}
      <span className="flex-1">{label}</span>
      {!dim && <Check size={12} className="text-slate-400" />}
    </button>
  );
}

export default SendMenu;
